/**
 * The tenant's brand at the top of a shell — Step 183.
 *
 * Kai, of a desk with a logo AND a tab icon uploaded: *"the icon and logo
 * don't appear anywhere… the name of the helpdesk always goes onto …"*. Both
 * shells drew a hardcoded lifebuoy and the app name as text, and never touched
 * the uploaded images — so the branding a customer set on the very first screen
 * showed up nowhere, and a long name ("Street Kids Direct - Support") truncated
 * to an ellipsis in the space a 60px sidebar leaves for it.
 *
 * The rule, in order of preference:
 *  - a LOGO (a wide wordmark) stands alone when there is room — it carries the
 *    name itself, so no truncating text beside it;
 *  - otherwise the square TAB ICON in a small box, with the name in text;
 *  - otherwise the lifebuoy, the same neutral default as before.
 *
 * `collapsed` is the narrow sidebar rail, where a wide logo cannot fit: it
 * falls back to the square icon so there is always SOMETHING, never a clipped
 * wordmark.
 */
import { LifeBuoy } from 'lucide-react';
import { useBranding } from '../../lib/useBranding';
import { cn } from '../../lib/utils';

export function BrandMark({ collapsed = false }: { collapsed?: boolean }) {
  const branding = useBranding();
  const name = branding?.appName || 'Support';

  // A wide logo, alone, when it has room. object-contain so any aspect ratio
  // letterboxes into the height rather than stretching.
  //
  // Step 184 — a light and a dark logo, chosen by CSS not JavaScript: a
  // wordmark for a light header vanishes on a dark one. Both are rendered and
  // the `dark:` variants show exactly one, so it tracks the theme toggle with
  // no reactive theme read. A desk with only one logo reuses it for both,
  // which is strictly better than a blank space in one theme.
  const light = branding?.logoPath ?? branding?.logoDarkPath ?? null;
  const dark = branding?.logoDarkPath ?? branding?.logoPath ?? null;
  if (!collapsed && light && dark) {
    return (
      <>
        <img src={light} alt={name} className="h-8 max-w-[12rem] object-contain dark:hidden" />
        <img src={dark} alt={name} className="hidden h-8 max-w-[12rem] object-contain dark:block" />
      </>
    );
  }

  return (
    <>
      <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-md bg-brand/15 text-brand">
        {branding?.faviconPath ? (
          <img src={branding.faviconPath} alt="" className="h-full w-full object-contain" />
        ) : (
          <LifeBuoy className="h-5 w-5" />
        )}
      </span>
      {!collapsed && <span className={cn('truncate text-sm font-semibold')}>{name}</span>}
    </>
  );
}
